<template>
  <div class="admin-supplier-attributions">
    <b-card>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          <feather-icon
            icon="LinkIcon"
            size="20"
            class="mr-1"
          />
          Attributions Fournisseurs
        </h4>
        <b-button
          variant="primary"
          size="sm"
          @click="showCreateModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-1"
          />
          Nouvelle Attribution
        </b-button>
      </b-card-header>

      <b-card-body>
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="4">
            <b-form-group label="Commercial">
              <b-form-select
                v-model="filters.salesperson"
                :options="salespersonOptions"
                @change="loadAttributions"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Statut">
              <b-form-select
                v-model="filters.active_only"
                :options="[
                  { value: 'false', text: 'Toutes' },
                  { value: 'true', text: 'Actives uniquement' },
                ]"
                @change="loadAttributions"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div
          v-if="loading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
        </div>

        <b-table
          v-else
          :items="attributions"
          :fields="fields"
          responsive
          hover
        >
          <template #cell(supplier_name)="data">
            <span class="font-weight-bold">{{ data.item.supplier_name }}</span>
            <br>
            <small class="text-muted">{{ data.item.supplier_phone }}</small>
          </template>

          <template #cell(salesperson_name)="data">
            <span>{{ data.item.salesperson_name }}</span>
            <br>
            <small class="text-muted">{{ data.item.salesperson_email }}</small>
          </template>

          <template #cell(period)="data">
            {{ formatDate(data.item.start_date) }}
            <span v-if="data.item.end_date">
              - {{ formatDate(data.item.end_date) }}
            </span>
            <span
              v-else
              class="text-success"
            >
              → En cours
            </span>
          </template>

          <template #cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">
              {{ data.item.is_active ? 'Actif' : 'Terminé' }}
            </b-badge>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <!-- Create Modal -->
    <b-modal
      v-model="showCreateModal"
      title="Nouvelle Attribution"
      hide-footer
    >
      <b-form @submit.prevent="createAttribution">
        <b-form-group label="Fournisseur">
          <b-form-select
            v-model="newAttribution.supplier"
            :options="supplierOptions"
            required
          />
        </b-form-group>

        <b-form-group label="Commercial">
          <b-form-select
            v-model="newAttribution.salesperson"
            :options="salespersonOptionsForCreate"
            required
          />
        </b-form-group>

        <b-form-group label="Date de début">
          <b-form-input
            v-model="newAttribution.start_date"
            type="date"
            required
          />
        </b-form-group>

        <b-alert
          show
          variant="info"
          class="mb-3"
        >
          <small>
            Si ce fournisseur est déjà attribué à un commercial, l'attribution
            précédente sera automatiquement terminée.
          </small>
        </b-alert>

        <div class="d-flex justify-content-end gap-2">
          <b-button
            variant="outline-secondary"
            @click="showCreateModal = false"
          >
            Annuler
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="creating"
          >
            {{ creating ? 'Création...' : 'Créer' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BForm,
  BModal,
  BAlert,
} from 'bootstrap-vue'

export default {
  name: 'AdminSupplierAttributions',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BForm,
    BModal,
    BAlert,
  },
  data() {
    return {
      loading: false,
      creating: false,
      attributions: [],
      salespeople: [],
      suppliers: [],
      showCreateModal: false,
      filters: {
        salesperson: '',
        active_only: 'true',
      },
      newAttribution: {
        supplier: '',
        salesperson: '',
        start_date: new Date().toISOString().split('T')[0],
      },
      fields: [
        { key: 'supplier_name', label: 'Fournisseur' },
        { key: 'salesperson_name', label: 'Commercial' },
        { key: 'period', label: 'Période' },
        { key: 'is_active', label: 'Statut', class: 'text-center' },
      ],
    }
  },
  computed: {
    salespersonOptions() {
      const options = [{ value: '', text: 'Tous les commerciaux' }]
      this.salespeople.forEach(sp => {
        options.push({ value: sp.id, text: sp.username })
      })
      return options
    },
    salespersonOptionsForCreate() {
      const options = [{ value: '', text: 'Sélectionner un commercial' }]
      this.salespeople.forEach(sp => {
        options.push({ value: sp.id, text: sp.username })
      })
      return options
    },
    supplierOptions() {
      const options = [{ value: '', text: 'Sélectionner un fournisseur' }]
      this.suppliers.forEach(s => {
        options.push({ value: s.id, text: `${s.username} (${s.phone})` })
      })
      return options
    },
  },
  mounted() {
    this.loadSalespeople()
    this.loadSuppliers()
    this.loadAttributions()
  },
  methods: {
    async loadSalespeople() {
      try {
        const response = await axios.get('/api/auth/users/', {
          params: { role: 'sales' },
        })
        this.salespeople = response.data.results || response.data
      } catch (error) {
        console.error('Error loading salespeople:', error)
      }
    },
    async loadSuppliers() {
      try {
        const response = await axios.get('/api/auth/users/', {
          params: { role: 'supplier' },
        })
        this.suppliers = response.data.results || response.data
      } catch (error) {
        console.error('Error loading suppliers:', error)
      }
    },
    async loadAttributions() {
      this.loading = true
      try {
        const params = { active_only: this.filters.active_only }
        if (this.filters.salesperson) {
          params.salesperson = this.filters.salesperson
        }
        const response = await axios.get('/api/commissions/supplier-attributions/', { params })
        this.attributions = response.data.results || response.data
      } catch (error) {
        console.error('Error loading attributions:', error)
      } finally {
        this.loading = false
      }
    },
    async createAttribution() {
      this.creating = true
      try {
        await axios.post('/api/commissions/supplier-attributions/', this.newAttribution)
        this.$bvToast.toast('Attribution créée avec succès', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.showCreateModal = false
        this.newAttribution = {
          supplier: '',
          salesperson: '',
          start_date: new Date().toISOString().split('T')[0],
        }
        this.loadAttributions()
      } catch (error) {
        const message = error.response?.data?.detail || 'Erreur lors de la création'
        this.$bvToast.toast(message, {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.creating = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR')
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
